import { useState } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { addDays, format } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { Alert, ModalButtonWithForm, TargetSchedule } from 'components/common';
import {
  EmailMessageEditor,
  FormGroup,
  RadioGroup,
  Select
} from 'components/common/form';

import type { EmailTemplateData, Group } from 'shared-types';
import type { AddGroupCourseReq } from 'shared-types/requests';
import type { ShareGroup, UpdateShareGroupsObject } from './ShareGroups';

interface Props {
  availableGroups: ShareGroup[];
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  groups: Pick<Group, 'id'>[];
  loading: boolean;
  updateGroups: (updateObj: UpdateShareGroupsObject) => Promise<void>;
}

const ShareGroupsAdd = ({
  availableGroups,
  emailTemplates,
  emailTemplatesDefault,
  groups,
  loading,
  updateGroups
}: Props) => {
  const { dateNow: now } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig: { timeZone }
  } = useReduxState();

  const [showEmailMessageEditor, setShowEmailMessageEditor] = useState(false);

  const groupOptions = availableGroups.filter(
    (administeredGroup) =>
      groups && !groups.find((group) => group.id === administeredGroup.id)
  );

  // the group's own setting can't change anything while the course is
  // mandatory for everyone
  const mandatoryDisabledReasons = course.isMandatory
    ? [messages.courseMandatoryReasonCourse]
    : [];

  const handleModalHide = () => setShowEmailMessageEditor(false);

  const handleSendEmailNotificationChange = (value: string) => {
    setShowEmailMessageEditor(Boolean(value === 'yes'));
  };

  const submit = async (values: typeof initialValues) => {
    await api.post(`/admin/groups/${values.groupId}/courses`, {
      courseId: course.id,
      ...(mandatoryDisabledReasons.length
        ? {}
        : { isMandatory: values.isMandatory }),
      deadlineDate: values.deadlineDate
        ? toDate(`${format(values.deadlineDate, 'y-MM-dd')}T23:59:59`, {
            timeZone
          }).toISOString()
        : null,
      deadlineDurationUnit: values.deadlineDurationUnit,
      deadlineDurationValue: values.deadlineDurationValue,
      deadlineType: values.deadlineType
    } satisfies AddGroupCourseReq);

    let emailMessage = null;
    let emailMessageIsWarning = false;

    if (values.sendEmailNotification === 'yes') {
      const response = await api.post(
        `/admin/courses/${course.id}/groups-invite`,
        {
          emailContent: { ...values.email },
          groupId: values.groupId
        }
      );

      emailMessage = `${response.data.sentEmails} ${messages.commonSendEmailInvitesSuccess}`;

      const errorEmails = Object.keys(response.data.errors);

      if (errorEmails && errorEmails.length) {
        emailMessageIsWarning = true;
        emailMessage += `<br /><br /> ${messages.commonSendEmailInvitesError}`;
        errorEmails.forEach((errorEmail) => {
          emailMessage += `<br />${errorEmail}`;
        });
      }
    }

    await updateGroups({ showLoading: false });
    dispatch.addNotice({
      text: messages.commonAddGroupSuccess,
      type: 'success'
    });
    if (emailMessage) {
      dispatch.addNotice({
        text: emailMessage,
        type: emailMessageIsWarning ? 'warning' : 'success'
      });
    }
  };

  const validateOption = (value: number) => {
    return value ? undefined : messages.commonFieldRequired;
  };

  const initialValues = {
    deadlineDate: addDays(now, 1),
    deadlineDurationUnit: 'days' as const,
    deadlineDurationValue: 7,
    deadlineType: 'course-default' as const,
    email: { ...emailTemplatesDefault.email },
    isMandatory: Boolean(course.isMandatory),
    sendEmailNotification: 'no',
    ...(groupOptions && groupOptions.length
      ? { groupId: groupOptions[0].id }
      : {})
  };

  return (
    <>
      <ModalButtonWithForm
        buttonCategory="success"
        buttonDisabled={loading}
        buttonIcon="plus"
        buttonSize="header"
        buttonText={messages.commonAddGroup}
        initialValues={initialValues}
        modalActionText={messages.commonAdd}
        modalTitle={messages.commonAddGroup}
        onHide={handleModalHide}
        onSubmit={submit}
        pristineIsValid={groupOptions && groupOptions.length}
        {...(showEmailMessageEditor ? { modalWidth: 'wide' } : {})}
      >
        {() =>
          groupOptions && groupOptions.length ? (
            <div>
              <FormGroup htmlFor="groupId" label={messages.commonGroup}>
                <Select
                  field="groupId"
                  id="groupId"
                  isSearchable={true}
                  options={groupOptions.map((group) => ({
                    label: group.title,
                    value: group.id
                  }))}
                  validate={validateOption}
                  validateOnChange={true}
                />
              </FormGroup>

              <TargetSchedule
                id="share-groups-add"
                includeCourseDefaultOption={true}
                includeCourseDefaultValue={false}
                includeMandatoryOption={true}
                mandatoryDisabledReasons={mandatoryDisabledReasons}
              />

              <FormGroup label={messages.emailNotification}>
                <RadioGroup
                  field="sendEmailNotification"
                  onChange={handleSendEmailNotificationChange}
                  options={[
                    {
                      id: `share-groups-send-email-notification-no`,
                      label: messages.emailNotificationNo,
                      value: 'no'
                    },
                    {
                      id: `share-groups-send-email-notification-yes`,
                      label: messages.emailNotificationGroup,
                      value: 'yes'
                    }
                  ]}
                />
              </FormGroup>

              <div
                style={{
                  height: showEmailMessageEditor ? 'auto' : 0,
                  overflow: 'hidden'
                }}
              >
                <EmailMessageEditor
                  availableTemplates={emailTemplates}
                  id="share-groups-email-notification"
                  style={{ marginTop: 20 }}
                />
              </div>
            </div>
          ) : (
            <Alert text={messages.settingsGroupsAddEmpty} />
          )
        }
      </ModalButtonWithForm>
    </>
  );
};

export default ShareGroupsAdd;
